<template>
  <div
    v-if="byState"
    class="flex flex-wrap items-center gap-1.5"
    role="group"
    aria-label="Atalhos de estado do SLA"
    :data-testid="testId"
  >
    <button
      v-for="state in PRIORITY_SLA_STATES"
      :key="state"
      type="button"
      class="rounded-full"
      :aria-pressed="isActive(state)"
      :title="state === 'OVERDUE' ? 'Estourado: prazo vencido' : undefined"
      :data-testid="`${testId}-card-${state}`"
      @click="onSelect(state)"
    >
      <PrioritySlaStateBadge
        :state="state"
        :class="isActive(state) ? 'ring-2 ring-primary ring-offset-1' : ''"
        :test-id="`${testId}-badge-${state}`"
      >
        {{ PRIORITY_SLA_STATE_LABELS[state] }}
        <span class="font-mono tabular-nums">{{ byState[state] ?? 0 }}</span>
      </PrioritySlaStateBadge>
    </button>
    <button
      v-if="overdueCarryOver !== null"
      type="button"
      class="inline-flex items-center gap-1 rounded-full border border-red-700 px-2 py-0.5 text-xs font-semibold text-red-700"
      :class="isOverdueOnly ? 'bg-red-50 ring-2 ring-primary ring-offset-1' : 'bg-white'"
      :aria-pressed="isOverdueOnly"
      title="Atrasados: À vista de ciclos anteriores (7 dias)"
      :data-testid="`${testId}-card-carry-over`"
      @click="emit('select-overdue')"
    >
      Atrasados <span class="font-mono tabular-nums">{{ overdueCarryOver }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import type { PrioritySlaState } from '@/Network/Types/Responses/PriorityPaymentsResponses'

import { PRIORITY_SLA_STATES } from '../filters'
import { PRIORITY_SLA_STATE_LABELS } from '../slaFormat'
import PrioritySlaStateBadge from './PrioritySlaStateBadge.vue'

/**
 * Atalhos de estado do M3 (Q8/Q22). Os números vêm de `slaCounters.byState`
 * (respeitam aba e `slaBand`, ignoram o próprio filtro). Clicar SUBSTITUI o
 * filtro de estado por aquele único estado; clicar de novo limpa. A
 * multi-seleção fica no seletor "Estado do SLA" da barra.
 */
const props = withDefaults(
  defineProps<{
    byState: Record<PrioritySlaState, number> | null
    /** Atrasados de ciclos anteriores; `null` fora do Ciclo atual. */
    overdueCarryOver: number | null
    activeStates: PrioritySlaState[]
    isOverdueOnly: boolean
    testId?: string
  }>(),
  { testId: 'priority-payments-counters' }
)

const emit = defineEmits<{
  'select-states': [states: PrioritySlaState[]]
  'select-overdue': []
}>()

const isActive = (state: PrioritySlaState): boolean =>
  props.activeStates.length === 1 && props.activeStates[0] === state

function onSelect(state: PrioritySlaState): void {
  emit('select-states', isActive(state) ? [] : [state])
}
</script>
